export type UtmParams = {
	utm_source?: string;
	utm_medium?: string;
	utm_campaign?: string;
	utm_term?: string;
	utm_content?: string;
};

const UTM_PARAM_KEYS: (keyof UtmParams)[] = [
	"utm_source",
	"utm_medium",
	"utm_campaign",
	"utm_term",
	"utm_content",
];

export const UTM_SESSION_COOKIE = "utm_session";
export const UTM_FIRST_TOUCH_COOKIE = "utm_first_touch";

// Matches GA4's default session window.
export const UTM_SESSION_TTL_SECONDS = 30 * 60;
// Pending marketing confirmation on the right attribution window.
export const UTM_FIRST_TOUCH_TTL_SECONDS = 90 * 24 * 60 * 60;

export function extractUtmParams(
	searchParams: URLSearchParams
): UtmParams | null {
	const params: UtmParams = {};
	let hasAny = false;
	for (const key of UTM_PARAM_KEYS) {
		const value = searchParams.get(key);
		if (value) {
			params[key] = value;
			hasAny = true;
		}
	}
	return hasAny ? params : null;
}

export function encodeUtmCookieValue(params: UtmParams): string {
	return JSON.stringify(params);
}

export function decodeUtmCookieValue(
	value: string | undefined | null
): UtmParams | null {
	if (!value) {
		return null;
	}
	try {
		const parsed: unknown = JSON.parse(value);
		if (parsed && typeof parsed === "object") {
			return parsed as UtmParams;
		}
		return null;
	} catch {
		return null;
	}
}

/**
 * Session (last-touch) cookie value: new UTM params in the request overwrite
 * it; otherwise the existing value is preserved so an in-session page view
 * with no UTM params doesn't blank out attribution captured earlier in the
 * same session. Returns null when there's nothing to write (no existing
 * cookie and no UTM params on this request).
 */
export function resolveSessionUtmCookieValue(
	searchParams: URLSearchParams,
	existingCookieValue: string | undefined
): string | null {
	const incoming = extractUtmParams(searchParams);
	if (incoming) {
		return encodeUtmCookieValue(incoming);
	}
	return existingCookieValue ?? null;
}

/**
 * Renames first-touch UTM params to a `first_touch_` prefix so they can sit
 * alongside the (last-touch) `utm_*` properties on the same event without
 * colliding — downstream analysis needs both: which channel is credited for
 * this session vs. which channel originally acquired the visitor.
 */
export function toFirstTouchProperties(
	params: UtmParams | null
): Record<string, string> {
	if (!params) {
		return {};
	}
	const result: Record<string, string> = {};
	for (const [key, value] of Object.entries(params)) {
		if (value !== undefined) {
			result[`first_touch_${key}`] = value;
		}
	}
	return result;
}

/**
 * First-touch cookie value: written once and never overwritten, so it keeps
 * crediting the channel that originally brought the visitor in regardless of
 * later campaign clicks in the same or a later session. Returns null once
 * the cookie already exists (nothing to write) or there's no UTM data yet.
 */
export function resolveFirstTouchUtmCookieValue(
	searchParams: URLSearchParams,
	existingCookieValue: string | undefined
): string | null {
	if (existingCookieValue) {
		return null;
	}
	const incoming = extractUtmParams(searchParams);
	return incoming ? encodeUtmCookieValue(incoming) : null;
}
